// Front Desk: the results of the organization's ads (the same report as Analítica), what to do next and what
// needs attention (GET /orgs/:orgId/front-desk). No fabricated data: billing does not exist yet and is shown as
// unavailable.
import { CompanyNotice } from "../commercial/company-notice";
import { FrontDeskResults } from "./results";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, CreditCard, Palette, Plus, Rocket, Tag } from "lucide-react";
import { useFrontDesk, useMe } from "@/api/hooks";
import { formatRelative } from "@/lib/format";
import { PRODUCTS, ROLE_LABELS, displayFormat } from "@/lib/labels";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import { EmptyState, PageHeader, Panel, Section } from "@/ui/layout";
import { ServingStateBadge, VersionStateBadge } from "@/components/status";
import { PageError, PageLoading, useTitle, useRouteParams } from "../common/page";

export function FrontDeskPage() {
  const { orgId } = useRouteParams("orgId");
  const { data: me } = useMe();
  const { data: fd, isLoading, error } = useFrontDesk(orgId);
  useTitle("Front Desk");
  if (isLoading) return <PageLoading />;
  if (error || !fd) return <PageError error={error} orgId={orgId} />;
  const firstName = (me?.user.displayName ?? "").split(" ")[0];
  const base = `/o/${orgId}`;
  const isNew = fd.recentCreatives.length === 0;

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow={`${fd.organization.name} · ${ROLE_LABELS[fd.membership.role]}`}
        title={firstName ? `Hola, ${firstName}` : "Front Desk"}
        description="Crea, publica y lleva tu Tag a tu plataforma publicitaria."
        actions={
          fd.capabilities.createCreative ? (
            <Button asChild variant="primary" size="lg">
              <Link to={`${base}/create`}>
                <Plus /> Crear creativo
              </Link>
            </Button>
          ) : null
        }
      />

      <CompanyNotice orgId={orgId} />

      {!isNew ? <FrontDeskResults orgId={orgId} /> : null}

      {isNew ? (
        <Panel className="grid gap-4 p-5">
          <h2 className="text-sm font-semibold">Tu primer anuncio en tres pasos</h2>
          <ol className="grid gap-3 sm:grid-cols-3">
            {[
              { icon: Palette, title: "Crea", text: "Elige el tipo de creativo y su formato." },
              { icon: Rocket, title: "Publica", text: "Revisa, aprueba y publica tu versión." },
              { icon: Tag, title: "Usa tu Tag", text: "Copia tu Tag permanente para tu plataforma publicitaria." },
            ].map((step, index) => (
              <li key={step.title} className="grid gap-1 rounded-md border border-line bg-sunken p-3">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <step.icon className="size-4 text-brand" aria-hidden="true" /> {index + 1}. {step.title}
                </span>
                <span className="text-sm text-muted">{step.text}</span>
              </li>
            ))}
          </ol>
          {!fd.capabilities.createCreative ? (
            <p className="text-sm text-muted">Tu rol no permite crear creativos. Pide acceso a un administrador.</p>
          ) : null}
        </Panel>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-[1fr_18rem]">
        <div className="grid content-start gap-8">
          {fd.servingIssues.length > 0 ? (
            <Section title="Requieren atención" description="Distribuciones que no están entregando y por qué.">
              <ul className="grid gap-2">
                {fd.servingIssues.map((issue) => (
                  <li key={issue.distributionId}>
                    <Link
                      to={`${base}/distributions/${issue.distributionId}`}
                      className="grid gap-1 rounded-md border border-line bg-surface px-3 py-2 hover:border-line-strong"
                    >
                      <span className="flex flex-wrap items-center gap-2 text-sm">
                        <AlertTriangle className="size-4 text-warning" aria-hidden="true" />
                        <span className="font-medium">{issue.creative.name}</span>
                        <span className="text-muted">
                          · {issue.name} · {issue.platform.name} {displayFormat(issue.format)}
                        </span>
                        <ServingStateBadge state={issue.effectiveState} />
                      </span>
                      <span className="text-xs text-muted">{issue.reasons.map((r) => r.message).join(" ")}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section
            title="Creativos recientes"
            actions={
              <Button asChild variant="ghost" size="sm">
                <Link to={`${base}/creatives`}>
                  Ver creatividades <ArrowRight />
                </Link>
              </Button>
            }
          >
            {fd.recentCreatives.length === 0 ? (
              <EmptyState
                icon={<Palette />}
                title="Aún no tienes creativos"
                description="Empieza con “Crear creativo”."
              />
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                {fd.recentCreatives.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`${base}/creatives/${c.id}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 hover:bg-sunken/60"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{c.name}</span>
                        <span className="block truncate text-xs text-muted">
                          {PRODUCTS[c.product].label} · {c.campaign.name}
                        </span>
                      </span>
                      {c.latestVersion ? (
                        <VersionStateBadge state={c.latestVersion.state} />
                      ) : (
                        <Badge>Sin versión</Badge>
                      )}
                      <span className="w-28 text-right text-xs text-muted">{formatRelative(c.lastActivityAt)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {fd.recentlyPublished.length > 0 ? (
            <Section title="Publicado recientemente">
              <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                {fd.recentlyPublished.map((p) => (
                  <li key={p.versionId} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                    <span className="min-w-0 flex-1">
                      <Link
                        to={`${base}/creatives/${p.creative.id}`}
                        className="block truncate font-medium hover:underline"
                      >
                        {p.creative.name}
                      </Link>
                      <span className="block text-xs text-muted">
                        Versión {p.versionNumber} ·{" "}
                        {p.activeDistributions > 0
                          ? `activa en ${String(p.activeDistributions)} distribución(es)`
                          : "no activa"}
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-xs text-muted">{formatRelative(p.publishedAt)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>

        <aside className="grid content-start gap-6" aria-label="Espacio de trabajo">
          {fd.campaigns.length > 0 ? (
            <Section title="Campañas activas">
              <ul className="grid gap-1">
                {fd.campaigns.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`${base}/campaigns/${c.id}`}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-sunken"
                    >
                      <span className="min-w-0 flex-1 truncate">{c.name}</span>
                      {c.paused ? <Badge tone="warning">Pausada</Badge> : null}
                      <span className="text-xs text-muted">{c.creatives}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          <Section title="Facturación">
            <div className="flex gap-2 rounded-md border border-dashed border-line-strong p-3 text-sm text-muted">
              <CreditCard className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              La facturación de InteractAds aún no está disponible en esta versión.
            </div>
          </Section>
        </aside>
      </div>
    </div>
  );
}
